if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.productInfo = this.closest('product-info');
        this.sizePromptText = 'PLEASE SELECT SIZE';
        this.sizeRequired = false;
        this.sizeConfirmed = true;
        this.sizePromptActive = false;
        this.selectedSizeValue = null;
        this.onSizeSelectionChange = this.handleSizeSelectionChange.bind(this);

        this.initSizeConfirmationState();
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.setProductStickyCommerceBodyClass();
        this.bindStickyCommerceButton();
        this.syncStickyCommerceButton();
        this.mountPdpStickyCommerceBar();
        this.bindStickyCommerceButton();
        this.bindStickyCommerceQuantity();
        this.syncStickyCommerceButton();
        this.initStickyCommerceVisibility();
      }

      disconnectedCallback() {
        if (this.productInfo && this.onSizeSelectionChange) {
          this.productInfo.removeEventListener('change', this.onSizeSelectionChange);
        }
        this.clearStickyCommerceMessageRotation();
        this.disconnectStickyCommerceObserver();
        this.unmountPdpStickyCommerceBar();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.sizeRequired && !this.sizeConfirmed) {
          this.activateSizePrompt();
          this.scrollToSizeSelection();
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        this.syncStickyCommerceButton();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              this.syncStickyCommerceButton();
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            this.syncStickyCommerceButton();

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
        this.syncStickyCommerceButton();
      }

      initSizeConfirmationState() {
        this.sizeRequired = !!this.getSizeFieldsets().length;
        this.sizeConfirmed = !this.sizeRequired;
        this.sizePromptActive = false;
        this.selectedSizeValue = null;

        if (!this.sizeRequired) return;

        this.clearSizeSelection();
        this.productInfo?.addEventListener('change', this.onSizeSelectionChange);
      }

      getSizeFieldsets() {
        if (!this.productInfo) return [];

        return Array.from(this.productInfo.querySelectorAll('.product-form__input--size-option'));
      }

      handleSizeSelectionChange(event) {
        if (!event.target?.closest('.product-form__input--size-option')) return;
        this.confirmSizeSelection(this.getSelectedSizeValue());
      }

      getSelectedSizeValue() {
        const sizeFieldset = this.getSizeFieldsets()[0];
        if (!sizeFieldset) return null;

        const checkedRadio = sizeFieldset.querySelector('input[type="radio"]:checked');
        if (checkedRadio) return this.normalizeStickyOptionValue(checkedRadio.value);

        const selectedOption = sizeFieldset.querySelector('select option[selected]');
        if (selectedOption) return this.normalizeStickyOptionValue(selectedOption.value || selectedOption.textContent);

        const select = sizeFieldset.querySelector('select');
        if (select?.selectedOptions?.[0]) {
          return this.normalizeStickyOptionValue(select.selectedOptions[0].value || select.selectedOptions[0].textContent);
        }

        return null;
      }

      clearSizeSelection(preserveInvalidState = false) {
        this.getSizeFieldsets().forEach((fieldset) => {
          fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
            input.checked = false;
            input.removeAttribute('checked');
          });

          fieldset.querySelectorAll('select').forEach((select) => {
            Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
            select.selectedIndex = -1;
          });

          const selectedValue = fieldset.querySelector('[data-selected-value]');
          if (selectedValue) selectedValue.textContent = '';

          if (!preserveInvalidState) {
            fieldset.removeAttribute('aria-invalid');
          }
        });
      }

      updateSizePromptText() {
        this.getSizeFieldsets().forEach((fieldset) => {
          const selectedValue = fieldset.querySelector('[data-selected-value]');
          if (!selectedValue) return;

          if (this.sizeRequired && this.sizePromptActive && !this.sizeConfirmed) {
            selectedValue.textContent = 'Please select a size';
          } else if (selectedValue.textContent.trim() === 'Please select a size') {
            selectedValue.textContent = '';
          }
        });
      }

      confirmSizeSelection(selectedValue = null) {
        if (!this.sizeRequired) return;

        this.selectedSizeValue = this.normalizeStickyOptionValue(selectedValue || this.getSelectedSizeValue()) || null;
        this.sizeConfirmed = true;
        this.sizePromptActive = false;
        this.getSizeFieldsets().forEach((fieldset) => fieldset.removeAttribute('aria-invalid'));
        this.updateSizePromptText();

        if (this.submitButtonText && !this.productInfo?.variantRequestPending) {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }

        this.syncStickyCommerceButton();
      }

      restoreSizeSelectionState() {
        if (!this.sizeRequired) return;

        const selectedSizeValue = this.normalizeStickyOptionValue(this.selectedSizeValue) || null;
        const hadExplicitSizeSelection = !!selectedSizeValue;
        const sizeFieldsets = this.getSizeFieldsets();

        if (!selectedSizeValue) {
          this.sizeConfirmed = false;
          this.clearSizeSelection(this.sizePromptActive && !this.sizeConfirmed);
          this.updateSizePromptText();
          this.syncStickyCommerceButton();
          return;
        }

        let matchedSelection = false;

        sizeFieldsets.forEach((fieldset) => {
          let fieldsetMatched = false;

          fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
            const isMatch = this.normalizeStickyOptionValue(input.value) === selectedSizeValue;
            input.checked = isMatch;
            if (isMatch) {
              input.setAttribute('checked', 'checked');
              fieldsetMatched = true;
            } else {
              input.removeAttribute('checked');
            }
          });

          fieldset.querySelectorAll('select').forEach((select) => {
            let selectMatched = false;
            Array.from(select.options).forEach((option) => {
              const isMatch =
                this.normalizeStickyOptionValue(option.value || option.textContent) === selectedSizeValue;
              option.selected = isMatch;
              if (isMatch) {
                option.setAttribute('selected', 'selected');
                select.selectedIndex = option.index;
                selectMatched = true;
                fieldsetMatched = true;
              } else {
                option.removeAttribute('selected');
              }
            });

            if (!selectMatched) {
              select.selectedIndex = -1;
            }
          });

          const selectedValue = fieldset.querySelector('[data-selected-value]');
          if (selectedValue) {
            selectedValue.textContent = fieldsetMatched ? selectedSizeValue : '';
          }

          if (!fieldsetMatched) {
            fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
              input.checked = false;
              input.removeAttribute('checked');
            });
          }

          matchedSelection = matchedSelection || fieldsetMatched;
        });

        if (!matchedSelection) {
          this.selectedSizeValue = null;
          this.sizeConfirmed = false;
          if (hadExplicitSizeSelection) {
            this.sizePromptActive = true;
          }
          this.clearSizeSelection(this.sizePromptActive);
        } else {
          this.sizeConfirmed = true;
        }

        this.updateSizePromptText();
        this.syncStickyCommerceButton();
      }

      activateSizePrompt() {
        if (!this.sizeRequired || this.sizeConfirmed) return;

        this.sizePromptActive = true;
        this.getSizeFieldsets().forEach((fieldset) => fieldset.setAttribute('aria-invalid', 'true'));
        this.updateSizePromptText();

        if (this.submitButtonText) {
          this.submitButtonText.textContent = this.sizePromptText;
        }

        this.syncStickyCommerceButton();
      }

      scrollToSizeSelection() {
        const sizeFieldset = this.getSizeFieldsets()[0];
        if (!sizeFieldset) return;

        sizeFieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const firstControl = sizeFieldset.querySelector('input:not([disabled]), select:not([disabled])');
        if (firstControl) {
          requestAnimationFrame(() => {
            firstControl.focus({ preventScroll: true });
          });
        }
      }

      getStickyBar() {
        const internalBar = this.querySelector('[data-sticky-commerce-bar]');
        if (internalBar) return internalBar;

        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          return quickAddModal.querySelector('[data-quick-add-modal-footer] [data-sticky-commerce-bar]');
        }

        const ownerId = this.dataset.sectionId || this.closest('product-info')?.id || 'main-product';
        return document.querySelector(`[data-pdp-sticky-commerce-owner="${ownerId}"]`);
      }

      getStickyTrigger() {
        const stickyBar = this.getStickyBar();
        return stickyBar ? stickyBar.querySelector('[data-sticky-commerce-trigger]') : null;
      }

      getStickyPrice() {
        const stickyBar = this.getStickyBar();
        return stickyBar ? stickyBar.querySelector('[data-sticky-commerce-price]') : null;
      }

      getStickySelection() {
        const stickyBar = this.getStickyBar();
        return stickyBar ? stickyBar.querySelector('[data-sticky-commerce-selection]') : null;
      }

      getSubmitButton() {
        return this.querySelector('[data-sticky-commerce-source]') || this.submitButton;
      }

      getSubmitButtonText() {
        const sourceButton = this.getSubmitButton();
        const visibleText = Array.from(sourceButton.querySelectorAll('span'))
          .find((span) => !span.classList.contains('hidden') && span.textContent.trim());

        return visibleText?.textContent.trim() || sourceButton.textContent.trim();
      }

      getCurrentPriceText() {
        const productInfo = this.closest('product-info');
        const priceElement = productInfo?.querySelector(
          '.price__sale .price-item--sale, .price__regular .price-item--regular, .price .price-item'
        );

        return priceElement?.textContent.trim();
      }

      getQuantityInput() {
        const formId = this.form?.id;
        const productInfo = this.closest('product-info');

        return (
          (formId && productInfo?.querySelector(`.quantity__input[form="${formId}"]`)) ||
          productInfo?.querySelector('.quantity__input') ||
          this.form?.querySelector('[name="quantity"]')
        );
      }

      getProductTitleText() {
        const productInfo = this.closest('product-info');
        const titleElement = productInfo?.querySelector('.product__title h1, .product__title .h1, .product__title a');

        return titleElement?.textContent.trim();
      }

      normalizeStickyOptionValue(value) {
        const normalizedValue = value?.replace(/\s+/g, ' ').trim();
        if (!normalizedValue || normalizedValue.toLowerCase() === 'default title') return '';

        return normalizedValue;
      }

      getSelectedOptionText() {
        const productInfo = this.closest('product-info');
        const stickySelectionParts = [];
        const optionValues = [];
        const priceText = this.getCurrentPriceText();

        if (priceText) stickySelectionParts.push(priceText);

        productInfo?.querySelectorAll('.product-form__option-selected[data-selected-value]').forEach((element) => {
          const optionValue = this.normalizeStickyOptionValue(element.textContent);
          if (optionValue && !optionValues.includes(optionValue)) optionValues.push(optionValue);
        });

        if (!optionValues.length) {
          productInfo?.querySelectorAll('fieldset input:checked').forEach((input) => {
            const optionValue = this.normalizeStickyOptionValue(input.value);
            if (optionValue && !optionValues.includes(optionValue)) optionValues.push(optionValue);
          });
        }

        if (!optionValues.length) {
          productInfo?.querySelectorAll('select').forEach((select) => {
            const optionValue = this.normalizeStickyOptionValue(select.selectedOptions?.[0]?.textContent || select.value);
            if (optionValue && !optionValues.includes(optionValue)) optionValues.push(optionValue);
          });
        }

        const quantity = parseInt(this.getQuantityInput()?.value, 10);
        if (quantity > 1) optionValues.push(`Qty ${quantity}`);

        stickySelectionParts.push(...optionValues);

        return stickySelectionParts.join(' \u00b7 ') || this.getProductTitleText() || '';
      }

      getStickyMessages(messageElement) {
        return (messageElement?.dataset.stickyCommerceMessages || messageElement?.textContent || '')
          .split(/\r?\n/)
          .map((message) => message.trim())
          .filter(Boolean);
      }

      clearStickyCommerceMessageRotation() {
        if (this.stickyCommerceMessageInterval) clearInterval(this.stickyCommerceMessageInterval);
        if (this.stickyCommerceMessageTimeout) clearTimeout(this.stickyCommerceMessageTimeout);

        this.stickyCommerceMessageInterval = null;
        this.stickyCommerceMessageTimeout = null;

        if (this.stickyCommerceMessageElement) {
          this.stickyCommerceMessageElement.dataset.rotationStarted = 'false';
          this.stickyCommerceMessageElement.classList.remove('is-changing');
        }
      }

      initStickyCommerceMessageRotation(stickyBar) {
        const messageElement = stickyBar?.querySelector('[data-sticky-commerce-message]');
        if (!messageElement) return;

        const messages = this.getStickyMessages(messageElement);
        if (!messages.length) {
          messageElement.hidden = true;
          this.clearStickyCommerceMessageRotation();
          return;
        }

        if (this.stickyCommerceMessageElement && this.stickyCommerceMessageElement !== messageElement) {
          this.clearStickyCommerceMessageRotation();
        }

        this.stickyCommerceMessageElement = messageElement;
        messageElement.hidden = false;

        if (messages.length === 1) {
          this.clearStickyCommerceMessageRotation();
          messageElement.textContent = messages[0];
          return;
        }

        if (messageElement.dataset.rotationStarted === 'true') return;

        let messageIndex = 0;
        messageElement.textContent = messages[0];
        messageElement.dataset.rotationStarted = 'true';
        this.stickyCommerceMessageInterval = setInterval(() => {
          messageIndex = (messageIndex + 1) % messages.length;
          messageElement.classList.add('is-changing');
          this.stickyCommerceMessageTimeout = setTimeout(() => {
            messageElement.textContent = messages[messageIndex];
            messageElement.classList.remove('is-changing');
          }, 180);
        }, 3600);
      }

      bindStickyCommerceQuantity() {
        const quantityInput = this.getQuantityInput();
        if (!quantityInput || quantityInput.dataset.stickyCommerceQuantityBound === 'true') return;

        quantityInput.dataset.stickyCommerceQuantityBound = 'true';
        quantityInput.addEventListener('change', () => this.syncStickyCommerceButton());
        quantityInput.addEventListener('input', () => this.syncStickyCommerceButton());
      }

      bindStickyCommerceButton() {
        const stickyTrigger = this.getStickyTrigger();
        if (!stickyTrigger || stickyTrigger.dataset.stickyCommerceBound === 'true') return;

        stickyTrigger.dataset.stickyCommerceBound = 'true';
        stickyTrigger.addEventListener('click', () => {
          const sourceButton = this.getSubmitButton();
          if (!sourceButton || sourceButton.disabled || sourceButton.getAttribute('aria-disabled') === 'true') return;

          sourceButton.click();
        });
      }

      syncStickyCommerceButton() {
        const stickyBar = this.getStickyBar();
        const stickyTrigger = this.getStickyTrigger();
        const sourceButton = this.getSubmitButton();
        if (!stickyBar || !stickyTrigger || !sourceButton) return;

        this.bindStickyCommerceQuantity();

        const isDisabled = sourceButton.disabled || sourceButton.getAttribute('aria-disabled') === 'true';
        const isLoading = sourceButton.classList.contains('loading');
        const stickyText = stickyTrigger.querySelector('[data-sticky-commerce-text]');
        const stickySelection = this.getStickySelection();

        stickyTrigger.disabled = isDisabled;
        stickyTrigger.toggleAttribute('aria-disabled', isDisabled);
        stickyTrigger.classList.toggle('is-loading', isLoading);

        if (this.sizeRequired && this.sizePromptActive && !this.sizeConfirmed) {
          if (this.submitButtonText) this.submitButtonText.textContent = this.sizePromptText;
          if (stickyText) stickyText.textContent = this.sizePromptText;
        } else if (stickyText) {
          stickyText.textContent = this.getSubmitButtonText();
        }
        if (stickySelection) stickySelection.textContent = this.getSelectedOptionText();
        this.initStickyCommerceMessageRotation(stickyBar);
      }

      setProductStickyCommerceBodyClass() {
        const productInfo = this.closest('product-info[data-update-url="true"]');
        if (productInfo && !this.closest('quick-add-modal')) {
          document.body.classList.add('template-product-sticky-commerce');
        }
      }

      disconnectStickyCommerceObserver() {
        if (!this.stickyCommerceObserver) return;

        this.stickyCommerceObserver.disconnect();
        this.stickyCommerceObserver = null;
      }

      unmountPdpStickyCommerceBar() {
        if (this.closest('quick-add-modal')) return;

        const ownerId = this.dataset.sectionId || this.closest('product-info')?.id || 'main-product';
        const portal = document.querySelector('[data-pdp-sticky-commerce-portal]');
        const stickyBar = portal?.querySelector(`[data-pdp-sticky-commerce-owner="${ownerId}"]`);

        if (stickyBar) stickyBar.remove();
        if (portal && !portal.querySelector('[data-sticky-commerce-bar]')) portal.remove();
        if (!document.querySelector('product-info[data-update-url="true"]')) {
          document.body.classList.remove('template-product-sticky-commerce');
        }
      }

      mountPdpStickyCommerceBar() {
        if (this.closest('quick-add-modal')) return;

        const productInfo = this.closest('product-info[data-update-url="true"]');
        if (!productInfo) return;

        const stickyBar = this.querySelector('[data-sticky-commerce-bar]');
        if (!stickyBar) return;

        const ownerId = this.dataset.sectionId || productInfo.id || 'main-product';
        let portal = document.querySelector('[data-pdp-sticky-commerce-portal]');

        if (!portal) {
          portal = document.createElement('div');
          portal.setAttribute('data-pdp-sticky-commerce-portal', '');
          document.body.appendChild(portal);
        }

        portal.innerHTML = '';
        stickyBar.setAttribute('data-pdp-sticky-commerce-owner', ownerId);
        portal.appendChild(stickyBar);
      }

      initStickyCommerceVisibility() {
        if (this.closest('quick-add-modal')) return;

        const mobileQuery = window.matchMedia('(max-width: 749px)');
        const stickyBar = this.getStickyBar();
        const buttons = this.querySelector('.product-form__buttons');

        if (!stickyBar || !buttons) return;

        if (mobileQuery.matches) {
          stickyBar.classList.add('is-visible');
          return;
        }

        if (!window.IntersectionObserver) {
          stickyBar.classList.add('is-visible');
          return;
        }

        if (this.stickyCommerceObserver) {
          this.stickyCommerceObserver.disconnect();
        }

        const setStickyVisibility = (isButtonsVisible) => {
          stickyBar.classList.toggle('is-visible', !isButtonsVisible);
        };

        this.stickyCommerceObserver = new IntersectionObserver(
          ([entry]) => {
            setStickyVisibility(entry.isIntersecting);
          },
          {
            threshold: 0.22,
          }
        );

        this.stickyCommerceObserver.observe(buttons);

        requestAnimationFrame(() => {
          const rect = buttons.getBoundingClientRect();
          const isButtonsVisible = rect.bottom > 0 && rect.top < window.innerHeight;
          setStickyVisibility(isButtonsVisible);
        });
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
